import React from 'react';
import { format } from 'date-fns';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { formatCurrency } from '@/lib/utils';
import { getCustomerDisplayName } from '@/utils/customerDisplayHelpers';

interface CreditNoteRegularPreviewProps {
  creditNote: {
    credit_note_number: string;
    credit_amount: number;
    description?: string | null;
    source_type: string;
    status: string;
    credit_note_issued_at: string;
    source_invoice?: {
      invoice_number?: string;
    } | null;
  };
  customer: {
    name: string;
    last_name: string;
    customer_type: string;
    business_name?: string | null;
    street_address?: string | null;
    suburb?: string | null;
    city?: string | null;
    province_state?: string | null;
    postal_code?: string | null;
    emails?: string[];
    cell_numbers?: string[];
  };
  company: {
    name: string;
    email?: string | null;
    company_phone_number?: string | null;
    street_address?: string | null;
    suburb?: string | null;
    city?: string | null;
    province_state?: string | null;
    postal_code?: string | null;
    vat_number?: string | null;
    'Account Holder'?: string | null;
    'Bank Name'?: string | null;
    'Account Number'?: string | null;
    'Branch Code'?: string | null;
    'Use Bank Info'?: boolean;
  };
}

const getStatusBadge = (status: string) => {
  switch (status) {
    case 'issued':
      return <Badge className="bg-green-600">Issued</Badge>;
    case 'applied':
      return <Badge variant="secondary">Applied</Badge>;
    case 'refunded':
      return <Badge className="bg-purple-600">Refunded</Badge>;
    case 'cancelled':
      return <Badge variant="destructive">Cancelled</Badge>;
    default:
      return <Badge variant="outline">{status}</Badge>;
  }
};

const CreditNoteRegularPreview: React.FC<CreditNoteRegularPreviewProps> = ({
  creditNote,
  customer,
  company,
}) => {
  const customerDisplayName = getCustomerDisplayName(customer);

  const getFormattedAddress = (entity: any) => {
    const lines = [];
    if (entity?.street_address) lines.push(entity.street_address);
    if (entity?.suburb) lines.push(entity.suburb);
    if (entity?.city) lines.push(entity.city);
    if (entity?.postal_code) lines.push(entity.postal_code);
    return lines;
  };

  const getSourceDescription = () => {
    if (creditNote.source_type === 'overpayment' && creditNote.source_invoice?.invoice_number) {
      return `Overpayment on Invoice #${creditNote.source_invoice.invoice_number}`;
    }
    return creditNote.description || 'Credit issued';
  };

  return (
    <div className="bg-white text-black min-h-full p-8 pb-16 flex flex-col gap-10 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex flex-wrap justify-between items-center mb-6">
        <div className="flex items-center gap-4">
          <div className="text-2xl font-bold text-primary">
            Credit Note {creditNote.credit_note_number}
          </div>
          {getStatusBadge(creditNote.status)}
        </div>
        <div className="text-right space-y-1">
          <div className="text-gray-600 text-xs">
            Issued: {format(new Date(creditNote.credit_note_issued_at), 'dd MMMM yyyy')}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Left Column */}
        <div className="md:col-span-1 flex flex-col gap-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-primary text-lg">Company Info</CardTitle>
            </CardHeader>
            <CardContent className="pb-2">
              <div className="font-semibold">{company.name}</div>
              {getFormattedAddress(company).map((line, i) => (
                <div key={i} className="text-sm text-gray-700">{line}</div>
              ))}
              {company.email && <div className="text-sm">{company.email}</div>}
              {company.company_phone_number && <div className="text-sm">{company.company_phone_number}</div>}
              {company.vat_number && <div className="text-sm text-gray-600">VAT: {company.vat_number}</div>}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-blue-800 text-lg">Credit Issued To</CardTitle>
            </CardHeader>
            <CardContent className="pb-2">
              <div className="font-semibold">{customerDisplayName}</div>
              {getFormattedAddress(customer).map((line, i) => (
                <div key={i} className="text-sm text-gray-700">{line}</div>
              ))}
              {customer.cell_numbers?.[0] && <div className="text-sm">{customer.cell_numbers[0]}</div>}
              {customer.emails?.[0] && <div className="text-sm">{customer.emails[0]}</div>}
            </CardContent>
          </Card>
        </div>

        {/* Right Column */}
        <div className="md:col-span-2 flex flex-col gap-6">
          <Card>
            <CardHeader>
              <CardTitle>Credit Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Description:</span>
                <span>{getSourceDescription()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Source Type:</span>
                <span className="capitalize">{creditNote.source_type.replace('_', ' ')}</span>
              </div>
              {creditNote.source_invoice?.invoice_number && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Source Invoice:</span>
                  <span>#{creditNote.source_invoice.invoice_number}</span>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Credit Amount</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex justify-between font-bold text-lg border-t pt-2">
                <span>Total Credit:</span>
                <span className="text-green-700">{formatCurrency(creditNote.credit_amount)}</span>
              </div>
            </CardContent>
          </Card>

          {company['Use Bank Info'] && company['Account Number'] && creditNote.status === 'issued' && (
            <Card>
              <CardHeader>
                <CardTitle>Bank Details (for refund)</CardTitle>
              </CardHeader>
              <CardContent className="space-y-1 text-sm">
                <div><strong>Bank:</strong> {company['Bank Name']}</div>
                <div><strong>Account Holder:</strong> {company['Account Holder']}</div>
                <div><strong>Account Number:</strong> {company['Account Number']}</div>
                <div><strong>Branch Code:</strong> {company['Branch Code']}</div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreditNoteRegularPreview;
